import { accountApi } from 'api/account/account.api';
import { AccountAddOnsAPI, getAddOnBankRedirectUrl } from 'api/account/add-ons.api';
import { AccountOnboardingApi } from 'api/account/onboarding.api';
import { AccountReportSettingApi } from 'api/account/report_setting.api';
import { resourcePermissionsApi } from 'api/account/resource-permissions.api';
import {
  AccountUserPropertiesApi,
  UserAccountPropertiesApi,
} from 'api/account/user-account-properties.api';
import type { HourColumn, TaskColumn } from 'constants/export';
import { errorMessageFactory } from 'factories/api';
import { omit } from 'lodash-es';
import { fetchUsersSmartly } from 'redux/modules/users.actions';
import { createXhrStatusModule, xhrStatusQueryInfo } from 'redux/reusable/xhrStatus';
import {
  getCurrentAccount,
  getHasResourcePermissionsDraftChanged,
  getResourcePermissionsDraft,
  getXhrStatus,
} from 'redux/selectors/accounts';
import { getUserPermissions } from 'redux/selectors/user';
import type {
  Account,
  AccountUserProperty,
  ClientSideAccountUserProperty,
  RawAddOn,
  SubscriptionPlan,
  UserAccountProperty,
} from 'types/account';
import type { Thunk } from 'types/redux';
import type { UsersDraft } from 'types/users';
import { generateUserAccountPropertiesChangeSet, normalizeAccountPayload } from 'utils/account';
import { browserNavigation } from 'utils/browserNavigation';
import type { AccountPropertyRaw } from './accounts.types';
import * as types from './accounts.types';
import {
  addSubscriptionAddOn,
  fetchedSubscriptionAddOns,
  fetchedSubscriptionPlans,
  removeSubscriptionAddOn,
  setAccountProperties,
} from './accounts.types';
import { showToast } from './toasts.actions';
import { fetchUserPermissions } from './user.actions';

const DEFAULT_DISMISS_TIMEOUT = 3000;

const { wrapFetching, wrapSaving, ...xhrModule } = createXhrStatusModule('accounts');
export const wrapReducer = xhrModule.withXhrStatusReducer;

export function fetchAccountOnboardingProperties(): Thunk {
  return async dispatch => {
    try {
      const result = await AccountOnboardingApi.get();
      dispatch(setAccountProperties(result));
    } catch (err) {
      dispatch(
        showToast({
          message: 'Error fetching account properties',
          type: 'danger',
        })
      );
      throw err;
    }
  };
}

export function fetchAccountReportSettings(): Thunk<Promise<types.AccountReportSettingsPayload>> {
  return async dispatch => {
    try {
      const result = await dispatch(wrapFetching(AccountReportSettingApi.get()));
      dispatch(types.setAccountReportSettings(result));
      return result;
    } catch (err) {
      const message = errorMessageFactory(err);
      dispatch(
        showToast({
          message,
          type: 'danger',
          dismissAfter: DEFAULT_DISMISS_TIMEOUT,
        })
      );
      throw err;
    }
  };
}

export function fetchAccountUserProperties(): Thunk<Promise<Array<AccountUserProperty>>> {
  return async dispatch => {
    try {
      const result = await dispatch(wrapFetching(AccountUserPropertiesApi.get()));
      dispatch(types.setAccountUserProperties(result));
      return result;
    } catch (err) {
      const message = errorMessageFactory(err);
      dispatch(
        showToast({
          message,
          type: 'danger',
          dismissAfter: DEFAULT_DISMISS_TIMEOUT,
        })
      );
      throw err;
    }
  };
}

export function fetchAccountUserPropertiesPreviouslyUsedValues(): Thunk<
  Promise<types.UserPropertiesPreviouslyUsedValues>
> {
  return async dispatch => {
    try {
      const result = await dispatch(
        wrapFetching(UserAccountPropertiesApi.getPreviouslyUsedValues())
      );
      dispatch(types.setAccountUserPropertiesPreviouslyUsedValues(result));
      return result;
    } catch (err) {
      const message = errorMessageFactory(err);
      dispatch(
        showToast({
          message,
          type: 'danger',
          dismissAfter: DEFAULT_DISMISS_TIMEOUT,
        })
      );
      throw err;
    }
  };
}

export function saveUserAccountPropertyValues(draft: UsersDraft): Thunk<Promise<void>> {
  return async (dispatch, getState) => {
    if (!draft?.id || !draft?.userAccountProperties) return;
    const state = getState();
    const changeSet = generateUserAccountPropertiesChangeSet(
      state.accounts.userAccountProperties[draft.id],
      draft.userAccountProperties
    );
    try {
      return await dispatch(wrapSaving(UserAccountPropertiesApi.save(draft.id, changeSet)));
    } catch (err) {
      const message = errorMessageFactory(err);
      dispatch(
        showToast({
          message,
          type: 'danger',
          dismissAfter: DEFAULT_DISMISS_TIMEOUT,
        })
      );
      throw err;
    }
  };
}

export function fetchUserAccountProperties(
  userId: number
): Thunk<Promise<Array<UserAccountProperty>>> {
  return async dispatch => {
    try {
      const properties: Array<UserAccountProperty> = await dispatch(
        wrapFetching(UserAccountPropertiesApi.get(userId))
      );
      dispatch(
        types.setUserAccountProperties({
          userId,
          properties,
        })
      );
      return properties;
    } catch (err) {
      const message = errorMessageFactory(err);
      dispatch(
        showToast({
          message,
          type: 'danger',
          dismissAfter: DEFAULT_DISMISS_TIMEOUT,
        })
      );
      throw err;
    }
  };
}

export function fetchAccountReportSettingsAndCreateDraft(): Thunk {
  return async dispatch => {
    const result = await dispatch(fetchAccountReportSettings());

    dispatch(createReportSettingsDraft(result));
  };
}

export function fetchAccountUserPropertiesAndCreateDraft(): Thunk {
  return async dispatch => {
    await dispatch(fetchAccountUserProperties());
    dispatch(types.createAccountUserPropertiesDraft());
  };
}

export function saveAccountReportSettings(
  payload: types.AccountReportSettingsPayload
): Thunk<Promise<types.AccountReportSettingsPayload>> {
  return async dispatch => {
    try {
      const result = await dispatch(wrapSaving(AccountReportSettingApi.save(payload)));
      dispatch(types.setAccountReportSettings(result));
      return result;
    } catch (err) {
      const message = errorMessageFactory(err);
      dispatch(
        showToast({
          message,
          type: 'danger',
          dismissAfter: DEFAULT_DISMISS_TIMEOUT,
        })
      );
      throw err;
    }
  };
}

export function saveAccountUserProperties(
  payload: Array<ClientSideAccountUserProperty>
): Thunk<Promise<Array<AccountUserProperty>>> {
  return async dispatch => {
    try {
      return await dispatch(
        wrapSaving(AccountUserPropertiesApi.save(payload.map(val => omit(val, ['_clientSideId']))))
      );
    } catch (err) {
      const message = errorMessageFactory(err);
      dispatch(
        showToast({
          message,
          type: 'danger',
          dismissAfter: DEFAULT_DISMISS_TIMEOUT,
        })
      );
      throw err;
    }
  };
}

export function saveAccountProperties(properties: Array<AccountPropertyRaw>): Thunk<Promise<void>> {
  return async dispatch => {
    try {
      await AccountOnboardingApi.save(properties);
      dispatch(setAccountProperties(properties));
    } catch (err) {
      dispatch(
        showToast({
          message: 'Error fetching account properties',
          type: 'danger',
        })
      );
      throw err;
    }
  };
}

export function fetchAccounts(): Thunk<Promise<Array<Account>>> {
  return (dispatch, getState) => {
    const accounts = getState().accounts.entries;
    const accountId = getState().accounts.current.id;

    return dispatch(wrapFetching(accountApi.get()))
      .then(response => {
        const entries = response.map(normalizeAccountPayload);
        dispatch(
          types.fetched({
            entries,
            currentAccountId: accountId,
          })
        );
        return entries;
      })
      .catch(err => {
        const message = errorMessageFactory(err);
        dispatch(
          showToast({
            message,
            type: 'danger',
            dismissAfter: DEFAULT_DISMISS_TIMEOUT,
          })
        );
        return accounts;
      });
  };
}

export function createReportSettingsDraft(
  payload?: Partial<types.AccountReportSettingsPayload>
): Thunk {
  return (dispatch, getState) => {
    const accountReportSettings = getState().accounts.accountReportSettings;
    dispatch(types.startReportSettingsDraft({ ...accountReportSettings, ...payload }));
  };
}

export function saveDraft(): Thunk<Promise<Account | undefined>> {
  return async (dispatch, getState) => {
    const state = getState();
    const draft = state.accounts.draft;
    const current = state.accounts.current;
    const reportSettingsDraft = state.accounts.accountReportSettingsDraft;
    const accountUserPropertiesDraft = state.accounts.accountUserPropertiesDraft;
    const resourcePermissionsDraft = getResourcePermissionsDraft(state);
    const hasResourcePermissionsDraftChanged = getHasResourcePermissionsDraftChanged(state);

    if (!draft) {
      return Promise.resolve(current);
    }

    try {
      await dispatch(saveAccountUserProperties(accountUserPropertiesDraft));
      dispatch(fetchAccountUserPropertiesAndCreateDraft());
    } catch (err) {
      return;
    }

    if (reportSettingsDraft) {
      const savedSettings = await dispatch(saveAccountReportSettings(reportSettingsDraft));
      dispatch(types.updateReportSettingsDraft(savedSettings));
    }

    if (hasResourcePermissionsDraftChanged) {
      const result = await dispatch(saveResourcePermissionsDraft(resourcePermissionsDraft!));
      dispatch(types.createResourcePermissionsDraft(result));
    }

    return dispatch(wrapSaving(accountApi.save(draft)))
      .then(response => {
        const normalizedAccount = normalizeAccountPayload(response);
        dispatch(types.saved(normalizedAccount));

        if (draft.capacity.total_seconds !== current.capacity.total_seconds) {
          dispatch(fetchUsersSmartly('active', true));
        }
        dispatch(types.updateDraft(normalizedAccount));
        return normalizedAccount;
      })
      .then(res => {
        dispatch(
          showToast({
            message: 'Workspace settings saved',
            type: 'success',
            dismissAfter: DEFAULT_DISMISS_TIMEOUT,
          })
        );
        return res;
      })
      .catch(err => {
        const message = errorMessageFactory(err);
        dispatch(
          showToast({
            message,
            type: 'danger',
            dismissAfter: DEFAULT_DISMISS_TIMEOUT,
          })
        );
        return current;
      });
  };
}

export function deleteAccountIdAndRedirect(accountId: number): Thunk<Promise<unknown>> {
  return async () => {
    if (!accountId) return Promise.reject();
    return accountApi.delete(accountId).then(() => {
      // Account is deleted, force back to root of app and let redirects do their thing
      window.location.href = '/';
    });
  };
}

const emulatedFeaturesStorageKey = 'emulatedFeatures';

export const emulateFeatures =
  (properties: Partial<types.State['emulatedProperties']>): Thunk =>
  (dispatch, getState) => {
    try {
      sessionStorage.setItem(emulatedFeaturesStorageKey, JSON.stringify(properties));
    } finally {
      const originalAccount = getCurrentAccount(getState());
      dispatch(types.emulateFeatures(properties));
      const emulatedAccount = getCurrentAccount(getState());
      if (originalAccount.beta !== emulatedAccount.beta) {
        window.location.reload();
      }
    }
  };

export const stopEmulatingFeatures = (): Thunk => (dispatch, getState) => {
  try {
    sessionStorage.removeItem(emulatedFeaturesStorageKey);
  } finally {
    const emulatedAccount = getCurrentAccount(getState());
    dispatch(types.stopEmulatingFeatures());
    const originalAccount = getCurrentAccount(getState());
    if (emulatedAccount.beta !== originalAccount.beta) {
      window.location.reload();
    }
  }
};

export const fetchEmulatedFeaturesFromCache = (): Thunk => dispatch => {
  try {
    const serializedFeatures = sessionStorage.getItem(emulatedFeaturesStorageKey);
    if (!serializedFeatures) return;

    const properties: Partial<types.State['emulatedProperties']> = JSON.parse(serializedFeatures);
    dispatch(types.emulateFeatures(properties));
  } catch {}
};

export const fetchSubscriptionPlans = (): Thunk => async dispatch => {
  const subscriptionPlans: Array<SubscriptionPlan> = await dispatch(
    wrapFetching(
      // https://memoryai.slack.com/archives/CF77BDSGM/p1612898437007700?thread_ts=1612879200.000300&cid=CF77BDSGM
      fetch('https://api.timelyapp.com/1.1/plans.json')
        .then(r => r.json())
        .catch(() => [])
    )
  );
  dispatch(fetchedSubscriptionPlans(subscriptionPlans));
};

const addOnsFetchKey = 'addOns';

export function fetchAccountAddOns(): Thunk<Promise<void>> {
  return async dispatch => {
    const result = await dispatch(wrapFetching(AccountAddOnsAPI.list(), addOnsFetchKey));
    dispatch(fetchedSubscriptionAddOns(result));
  };
}

export function fetchAccountAddsSmartly(): Thunk<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState();

    const xhrStatus = xhrStatusQueryInfo(getXhrStatus(state), { key: addOnsFetchKey });
    if (!xhrStatus.shouldFetch) {
      return;
    }

    // Only users with `manage_subscription` can access the `add_ons` endpoints
    // https://memoryai.slack.com/archives/C04AT0ZPZ/p1631109135016100
    const permissions = getUserPermissions(state);
    if (!permissions.canManageSubscriptions) {
      return;
    }

    dispatch(fetchAccountAddOns());
  };
}

export function cancelAccountAddOn(addOn: Pick<RawAddOn, 'code'>): Thunk<Promise<void>> {
  return async dispatch => {
    try {
      const result = await dispatch(
        wrapFetching(AccountAddOnsAPI.cancel(addOn.code), `cancelling-${addOn.code}`)
      );
      dispatch(removeSubscriptionAddOn(result));
      dispatch(fetchAccounts());
    } catch (error) {
      dispatch(showToast({ type: 'danger', message: errorMessageFactory(error) }));
      throw error;
    }
  };
}

/**
 * Resolves 'redirected' when the card needs 3DS: the browser is then on its way to the bank, and
 * comes back to the subscription page, as from the ERB form.
 */
export function subscribeToAccountAddOn(
  addOn: Pick<RawAddOn, 'code'>
): Thunk<Promise<'subscribed' | 'redirected'>> {
  return async dispatch => {
    try {
      const result = await dispatch(
        wrapFetching(AccountAddOnsAPI.subscribe(addOn.code), `subscribing-${addOn.code}`)
      );
      dispatch(addSubscriptionAddOn(result));
      dispatch(fetchAccounts());
      return 'subscribed';
    } catch (error) {
      const bankRedirectUrl = getAddOnBankRedirectUrl(error);
      if (bankRedirectUrl) {
        browserNavigation.assign(bankRedirectUrl);
        return 'redirected';
      }
      dispatch(showToast({ type: 'danger', message: errorMessageFactory(error) }));
      throw error;
    }
  };
}

export function trialAccountAddOn(addOn: Pick<RawAddOn, 'code'>): Thunk<Promise<unknown>> {
  return async dispatch => {
    try {
      const result = await dispatch(
        wrapFetching(AccountAddOnsAPI.trial(addOn.code), `trialing-${addOn.code}`)
      );
      dispatch(addSubscriptionAddOn(result));
      dispatch(fetchAccounts());
    } catch (error) {
      dispatch(showToast({ type: 'danger', message: errorMessageFactory(error) }));
      throw error;
    }
  };
}

export function updateAccountReportColumnsOrder(columns: Array<HourColumn>): Thunk {
  return dispatch => {
    dispatch(types.updateReportSettingsDraft({ hours_column_order: columns }));
  };
}

export function updateAccountTaskReportColumnsOrder(columns: Array<TaskColumn>): Thunk {
  return dispatch => {
    dispatch(types.updateReportSettingsDraft({ tasks_column_order: columns }));
  };
}

export function fetchResourcePermissions(
  resource: types.AccountResourceType
): Thunk<Promise<types.ResourcePermissionsPayload>> {
  return async dispatch =>
    await dispatch(wrapFetching(resourcePermissionsApi.get(resource), resource)).then(
      resourcePermissions => {
        dispatch(types.fetchedSingleResourcePermissions(resourcePermissions));
        return resourcePermissions;
      }
    );
}

export function fetchDayLockingPermissionsAndCreateDraft(): Thunk {
  return async dispatch => {
    const result = await dispatch(fetchResourcePermissions('day_property'));
    dispatch(types.createResourcePermissionsDraft(result));
  };
}

export function saveResourcePermissionsDraft(
  draft: types.ResourcePermissionsPayload
): Thunk<Promise<types.ResourcePermissionsPayload>> {
  const payload: types.ResourcePermissionsUpdatePayload = {
    ...draft,
    permissions: Object.entries(draft.permissions).map(([roleId, operations]) => {
      return { role_id: +roleId, operations };
    }),
  };

  return async dispatch => {
    try {
      const resourcePermissions = await dispatch(wrapSaving(resourcePermissionsApi.save(payload)));
      dispatch(types.fetchedSingleResourcePermissions(resourcePermissions));
      dispatch(fetchUserPermissions());
      return resourcePermissions;
    } catch (err) {
      dispatch(
        showToast({
          message: 'Error saving permissions',
          type: 'danger',
        })
      );
      throw err;
    }
  };
}

export const { startOnboardingDraft, updateOnboardingDraft, clearOnboardingDraft } = types;
